import { queryOptions, useQuery } from '@tanstack/react-query';

import { POLLING_DELAY_MS } from '@cast/constants';
import { ApiResponse } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_OVERVIEW_QUERY_KEY = 'cluster-overview';

const apiFn = apiClient.costReport.getClusterOverview;

export type ClusterOverviewQueryData = ApiResponse<typeof apiFn>;

export { CLUSTER_OVERVIEW_QUERY_KEY };

export const getClusterOverviewQueryKey = ({
  clusterId,
  startTime,
  endTime,
  stepSeconds,
}: {
  clusterId: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
}) =>
  [
    CLUSTER_OVERVIEW_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    stepSeconds ?? null,
  ] as const;

export type UseClusterOverviewQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  stepSeconds?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
};

export const getClusterOverviewQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  stepSeconds,
  enabled = true,
  refetchInterval = POLLING_DELAY_MS,
}: UseClusterOverviewQueryArgs) =>
  queryOptions({
    queryKey: getClusterOverviewQueryKey({
      clusterId,
      startTime,
      endTime,
      stepSeconds,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        clusterId,
        startTime,
        endTime,
        stepSeconds,
      });
      return data;
    },
    enabled: enabled && !!clusterId && !!startTime && !!endTime,
    refetchInterval,
  });

export const useClusterOverviewQuery = (args: UseClusterOverviewQueryArgs) => {
  const { data, ...rest } = useQuery(getClusterOverviewQueryOptions(args));

  return { data, ...rest };
};
